import { useState } from 'react';
import { Plug, Plus } from 'lucide-react';
import { Button, EmptyState, unwrapAction } from '@eo/shared';

import { AddIntegrationDialog } from './AddIntegrationDialog';
import { INTEGRATIONS } from './constants';
import { statusOf } from './helpers';
import { IntegrationCard } from './IntegrationCard';
import { ManageIntegrationDialog } from './ManageIntegrationDialog';
import type { IntegrationDescriptor, IntegrationKey, IntegrationsPanelProps } from './types';

export function IntegrationsPanel({
  data,
  onSaveClickup,
  onRemoveClickup,
  onSaveGranola,
  onRemoveGranola,
  onDisconnectGoogle,
  onDisconnectSlack,
}: IntegrationsPanelProps) {
  const [adding, setAdding] = useState(false);
  const [managing, setManaging] = useState<IntegrationDescriptor | null>(null);
  const status = (key: IntegrationKey) => statusOf(data, key);
  const connected = INTEGRATIONS.filter((i) => status(i.key).connected);
  const available = INTEGRATIONS.filter((i) => !status(i.key).connected);

  const saveToken = async (key: IntegrationKey, token: string) => {
    const save = key === 'clickup' ? onSaveClickup : onSaveGranola;
    unwrapAction(await save({ token }), 'Saving failed. Try again.');
  };

  const remove = async (key: IntegrationKey) => {
    const action =
      key === 'google'
        ? onDisconnectGoogle
        : key === 'slack'
          ? onDisconnectSlack
          : key === 'clickup'
            ? onRemoveClickup
            : onRemoveGranola;
    unwrapAction(await action(), 'Disconnecting failed. Try again.');
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col justify-center gap-2">
          <h2 className="text-xl text-text-primary">Integrations</h2>
          <p className="text-sm text-text-tertiary">
            {connected.length} of {INTEGRATIONS.length} connected. These credentials are yours alone
            — they are stored per person and never shared with other staff.
          </p>
        </div>
        <Button
          className="w-fit shrink-0"
          onClick={() => setAdding(true)}
          disabled={available.length === 0}
          title={available.length === 0 ? 'Everything available is already connected.' : undefined}
        >
          <Plus className="size-4" />
          Add integration
        </Button>
      </div>

      {connected.length === 0 ? (
        <EmptyState
          icon={Plug}
          title="No integrations connected"
          description="Mission Control needs your ClickUp token before the review board and time tracking leave their setup states."
          action={<Button onClick={() => setAdding(true)}>Add integration</Button>}
        />
      ) : (
        <div className="grid max-w-[1600px] gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {connected.map((descriptor) => (
            <IntegrationCard
              key={descriptor.key}
              descriptor={descriptor}
              status={status(descriptor.key)}
              onManage={() => setManaging(descriptor)}
            />
          ))}
        </div>
      )}

      <AddIntegrationDialog
        open={adding}
        onOpenChange={setAdding}
        available={available}
        statusOf={status}
        onSaveToken={saveToken}
      />

      <ManageIntegrationDialog
        descriptor={managing}
        status={
          managing
            ? status(managing.key)
            : { connected: false, detail: null, verifiedAt: null, account: null }
        }
        onOpenChange={(open) => setManaging(open ? managing : null)}
        onSaveToken={saveToken}
        onRemove={remove}
      />
    </div>
  );
}
